import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Alert, useAlert } from '../../../components';
import { Store } from '../../../store';
import type { ScheduledEvent } from '../../event-details';
import { useRequesterContacts } from '../../event-details';
import type {
  ZendeskActionResponse,
  ZendeskAssignee,
  ZendeskAttachmentPayload,
  ZendeskIssueType
} from '../zendesk';
import { guardWrite, mockCreate, zendeskCall } from '../zendesk';
import { AttachmentsField } from './attachments-field';
import { buildBody, DescriptionFields, emptyNotes } from './description-fields';
import type { FieldOption, TicketDraft } from './ticket-fields';
import { TicketFields } from './ticket-fields';
import { TicketInfoGrid } from './ticket-info-grid';
import { useAttachments } from './use-attachments';

interface Props {
  scheduledEvent: ScheduledEvent;
  /** Loaded by the tab, not here: this card remounts on every open. */
  assignees: ZendeskAssignee[];
  issueTypes: ZendeskIssueType[];
  onClose: () => void;
  /** Filed successfully — the tab closes this card and reloads the list. */
  onCreated: () => void;
}

const CreateCard = styled.div`
  border: 1px solid rgba(120, 130, 255, 0.3);
  border-radius: 11px;
  background: rgba(78, 95, 253, 0.07);
  padding: 14px;
  margin-bottom: 13px;
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const CreateHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CreateTitle = styled.div`
  font-weight: 700;
  font-size: 13px;
  color: rgb(240, 242, 245);
`;

const CloseButton = styled.button`
  display: inline-flex;
  padding: 2px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(176, 186, 209, 0.7);

  i {
    font-size: 14px;
  }

  &:hover,
  &:focus {
    outline: none;
    color: var(--white);
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const NoteLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: 10px;
  color: rgba(176, 186, 209, 0.55);

  i {
    font-size: 12px;
    color: rgb(255, 210, 122);
  }
`;

const FooterActions = styled.div`
  display: inline-flex;
  gap: 8px;
`;

const CancelButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(213, 221, 238, 0.85);
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  padding: 9px 13px;
  cursor: pointer;

  &:hover,
  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const CreateButton = styled.button`
  background: rgb(78, 95, 253);
  color: rgb(255, 255, 255);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  padding: 9px 15px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover,
  &:focus {
    outline: none;
    opacity: 0.92;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

/*
 * The "New Zendesk ticket" form. It is mounted only while open, so closing it
 * discards the draft and releases the attachment previews — there is no reset
 * to keep in sync with the fields.
 */
export const CreateTicketCard = ({
  scheduledEvent,
  assignees,
  issueTypes,
  onClose,
  onCreated
}: Props): ReactElement => {
  const organization = scheduledEvent.location?.user?.organization_name ?? '';
  const eid = String(scheduledEvent.id);
  const cid = String(scheduledEvent.location?.user?.id ?? '');
  const secondary = scheduledEvent.location?.secondary_id ?? 0;
  const cidLabel = cid ? `${cid}.${secondary}` : '—';
  const eventType = scheduledEvent.event_type ?? '—';

  const [draft, setDraft] = useState<TicketDraft>(() => ({
    subject: `Command Center Ticket - CID: ${cidLabel} EID: ${eid}`,
    requester: '',
    assignee: '',
    issueType: '',
    priority: 'normal',
    serviceImpact: false
  }));

  const [notes, setNotes] = useState<Record<string, string>>(emptyNotes);
  const [creating, setCreating] = useState(false);

  const attachments = useAttachments();
  const createAlert = useAlert();

  // Command Center user filing the ticket — sent as `filedBy`.
  const me = Store.User.useMe();
  const filedBy = [me.first_name, me.last_name].filter(Boolean).join(' ');

  /*
   * The on-site technical contacts for this location, not user.contacts. That field reads the
   * legacy `contact` table, which is empty for any client whose people were migrated to sub-user
   * accounts with contact roles — their requester list came back empty. Filtering and de-duping
   * now happen in the hook, so all that is left here is the mapping to FieldOption.
   */
  const { contacts } = useRequesterContacts(scheduledEvent.location?.id);

  const requesterOptions = useMemo(
    (): FieldOption[] =>
      contacts.map((contact) => ({
        value: contact.email ?? '',
        label: [contact.first_name, contact.last_name].filter(Boolean).join(' ')
      })),
    [contacts]
  );

  const assigneeOptions = useMemo(
    (): FieldOption[] => assignees.map((agent) => ({ value: agent.key, label: agent.label })),
    [assignees]
  );

  const patchDraft = (patch: Partial<TicketDraft>): void => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const createTicket = (): void => {
    const blocked = guardWrite('create');

    if (blocked !== undefined) {
      createAlert.show(blocked);

      return;
    }

    if (creating) {
      return;
    }

    const body = buildBody(notes);

    // Zendesk rejects a ticket with no requester, no issue type or an empty note.
    const missing =
      draft.requester === ''
        ? 'Select a requester.'
        : draft.issueType === ''
        ? 'Select an issue type.'
        : body === ''
        ? 'Fill in at least one description field.'
        : undefined;

    if (missing !== undefined) {
      createAlert.show(missing);

      return;
    }

    const requesterName =
      requesterOptions.find((option) => option.value === draft.requester)?.label ?? '';

    setCreating(true);

    void (async (): Promise<void> => {
      try {
        const payload: ZendeskAttachmentPayload[] = await attachments.toPayload();

        const response = await zendeskCall<ZendeskActionResponse>(
          {
            action: 'create',
            eid,
            cid,
            eventName: scheduledEvent.name,
            eventType,
            subject: draft.subject,
            requesterEmail: draft.requester,
            requesterName,
            assignee: draft.assignee,
            issueType: draft.issueType,
            priority: draft.priority,
            serviceImpact: draft.serviceImpact,
            filedBy,
            body,
            attachments: payload
          },
          mockCreate
        );

        if (response.ok) {
          // Unmounts this card, so `creating` dies with it — no state to clear.
          onCreated();

          return;
        }

        createAlert.show(response.detail ?? response.reason ?? 'Failed to create the ticket.');
      } catch (error_: unknown) {
        createAlert.show(error_ instanceof Error ? error_.message : 'Failed to create the ticket.');
      }

      // Only reached when the ticket wasn't filed; on success we're already gone.
      setCreating(false);
    })();
  };

  return (
    <CreateCard
      onDrop={attachments.onDrop}
      onDragOver={attachments.onDragOver}
      onPaste={attachments.onPaste}
    >
      <CreateHead>
        <CreateTitle>New Zendesk ticket</CreateTitle>
        <CloseButton type='button' title='Close' onClick={onClose}>
          <i className='far fa-times' />
        </CloseButton>
      </CreateHead>

      <TicketInfoGrid
        cidLabel={cidLabel}
        organization={organization}
        eid={eid}
        eventType={eventType}
      />

      <TicketFields
        draft={draft}
        onChange={patchDraft}
        requesterOptions={requesterOptions}
        assigneeOptions={assigneeOptions}
        issueTypeOptions={issueTypes}
      />

      <DescriptionFields
        notes={notes}
        onChange={(key, value): void => {
          setNotes((prev) => ({ ...prev, [key]: value }));
        }}
      />

      <AttachmentsField
        attachments={attachments.items}
        inputRef={attachments.inputRef}
        onPickFiles={attachments.onPickFiles}
        onRemove={attachments.remove}
      />

      {createAlert.message !== undefined && (
        <Alert message={createAlert.message} onDismiss={createAlert.clear} />
      )}

      <Footer>
        <NoteLabel>
          <i className='fas fa-lock' />
          Posted as an internal note in Zendesk.
        </NoteLabel>

        <FooterActions>
          <CancelButton type='button' onClick={onClose}>
            Cancel
          </CancelButton>
          <CreateButton type='button' disabled={creating} onClick={createTicket}>
            {creating ? 'Creating…' : 'Create ticket'}
          </CreateButton>
        </FooterActions>
      </Footer>
    </CreateCard>
  );
};
